import {Component} from '@angular/core';
import {MatButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {Router, RouterLink} from "@angular/router";
import {ColComponent, ContainerComponent, RowComponent} from "@coreui/angular";
import {MatCheckbox} from "@angular/material/checkbox";
import {MatError, MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {PermissionsService} from 'src/app/services/permissions.service';
import {SnackbarService} from "../../../services/snackbar.service";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {IPermission} from "../../../interfaces/IPermission";
import {AsyncPipe, NgForOf, NgIf} from "@angular/common";
import {MatCard, MatCardContent} from "@angular/material/card";
import {map, Observable, startWith} from "rxjs";
import {IAddRoleWithPermissions} from "../../../interfaces/IAddRoleWithPermissions";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatStep, MatStepLabel, MatStepper, MatStepperNext, MatStepperPrevious} from "@angular/material/stepper";
import {MatChip, MatChipsModule} from "@angular/material/chips";
import {PermissionsSelectorComponent} from "../../../components/permissions-selector/permissions-selector.component";

@Component({
  selector: 'app-add-new-role',
  standalone: true,
  imports: [
    MatButton,
    MatIcon,
    RouterLink,
    ContainerComponent,
    MatCheckbox,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    NgForOf,
    NgIf,
    FormsModule,
    RowComponent,
    ColComponent,
    MatCard,
    MatCardContent,
    AsyncPipe,
    MatProgressSpinner,
    MatStepper,
    MatStep,
    MatStepLabel,
    MatStepperNext,
    MatStepperPrevious,
    MatChip,
    PermissionsSelectorComponent,
    MatChipsModule
  ],
  templateUrl: './add-new-role.component.html',
  styleUrl: './add-new-role.component.css'
})
export class AddNewRoleComponent {

  public availablePermissions: IPermission[] = [];
  public filteredPermissions$!: Observable<IPermission[]>;
  public selectedPermissions: IPermission[] = [];

  public uiState: UIStateEnum = UIStateEnum.ShowLoading;
  public uiStateEnumForTemplate = UIStateEnum;

  public roleNameControl: FormControl<string | null> = new FormControl<string | null>('', Validators.required);
  public roleDescriptionControl: FormControl<string | null> = new FormControl<string | null>('', Validators.required);
  public isCrossGameRoleControl: FormControl<boolean | null> = new FormControl<boolean>(false);
  public searchTextControl = new FormControl<string>('', {nonNullable: true});

  constructor(private permissionService: PermissionsService,
              private snackBarService: SnackbarService,
              private errorHandlingService: ErrorHandlingService,
              private router: Router,
  ) { }

  public createRoleForm = new FormGroup({
    roleName: this.roleNameControl,
    roleDescription: this.roleDescriptionControl,
    isCrossGameRole: this.isCrossGameRoleControl,
    searchText: this.searchTextControl
  });

  ngOnInit(): void {
    this.uiState = UIStateEnum.ShowLoading;

    this.permissionService.fetchAllAvailablePermissions().subscribe({
      next: (resP) => {
        this.availablePermissions = resP;

        this.filteredPermissions$ = this.searchTextControl.valueChanges.pipe(
          startWith(''),
          map(searchText => this._filter(searchText || ''))
        );

        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.errorHandlingService.displayDialogLevelErrorMessage(err);
        this.uiState = UIStateEnum.ShowData;
      }
    });
  }

  private _filter(searchText: string): IPermission[] {
    const filterValue = searchText.toLowerCase();
    return this.availablePermissions.filter(permission =>
      permission.Name.toLowerCase().includes(filterValue)
    );
  }

  public createRole() {
    if (!this.createRoleForm.valid) {
      this.createRoleForm.markAllAsTouched();
      return;
    }

    this.uiState = UIStateEnum.ShowLoading;

    const newRoleCommand: IAddRoleWithPermissions = {
      name: this.roleNameControl.value!,
      description: this.roleDescriptionControl.value!,
      isCrossGame: this.isCrossGameRoleControl.value!,
      permissionValues: this.selectedPermissions.map(p => p.Value)
    };

    this.permissionService.addNewRoleWithPermissions(newRoleCommand).subscribe({
      next: () => {
        this.snackBarService.openSuccessfulSnackBar('Role created successfully!');
        this.router.navigateByUrl('/manage-global-permissions');
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayDialogLevelErrorMessage(err, true);
      }
    });
  }
}